import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar, Sparkles, ChevronLeft, ChevronRight, User, Clock, CheckCircle2, AlertCircle, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CATEGORIES = [
  { name: 'Content Planning', color: 'bg-violet-100 text-violet-700 border-violet-300' },
  { name: 'Social Media', color: 'bg-pink-100 text-pink-700 border-pink-300' },
  { name: 'Video Content', color: 'bg-red-100 text-red-700 border-red-300' },
  { name: 'LinkedIn/Professional', color: 'bg-sky-100 text-sky-700 border-sky-300' },
  { name: 'Ads & Paid', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { name: 'Content Writing', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { name: 'General', color: 'bg-gray-100 text-gray-700 border-gray-300' },
];

const ContentCalendar = () => {
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'category'
  const [loading, setLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  // Create Task state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    week: 'Week 1',
    priority: 'med',
    broadCategory: 'Social Media',
    platform: 'Instagram + Facebook',
    contentType: 'Social Post',
    isBatchedTask: true,
    workflowType: 'static',
    postCount: 1
  });

  const fetchData = useCallback(async () => {
    try {
      const [accountsRes, templatesRes, membersRes, tasksRes] = await Promise.all([
        axios.get(`${API}/accounts`),
        axios.get(`${API}/content-templates`),
        axios.get(`${API}/team-members`),
        axios.get(`${API}/tasks`)
      ]);
      setAccounts(accountsRes.data);
      setTemplates(templatesRes.data);
      setTeamMembers(membersRes.data);
      setTasks(tasksRes.data);
      
      if (accountsRes.data.length > 0 && !selectedAccount) {
        setSelectedAccount(accountsRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
    // Note: selectedAccount intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMemberName = (memberId) => {
    const member = teamMembers.find(m => m.id === memberId);
    return member?.name || 'Unknown';
  };

  const getAccountTemplate = () => {
    return templates.find(t => t.accountId === selectedAccount);
  };

  const handleGenerateTasks = async () => {
    setIsGenerating(true);
    try {
      // Use accountId - backend will auto-create template if needed
      const response = await axios.post(`${API}/content-templates/generate-tasks`, {
        accountId: selectedAccount,
        month: selectedMonth,
        year: selectedYear
      });
      
      toast.success(response.data.message);
      setGenerateDialogOpen(false);
      
      // Refresh both tasks and templates (in case a new template was created)
      const [tasksRes, templatesRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/content-templates`)
      ]);
      setTasks(tasksRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error generating tasks:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate tasks');
    } finally {
      setIsGenerating(false);
    }
  };

  // Reset (delete) content calendar tasks for specific month
  const handleResetAllTasks = async () => {
    setIsResetting(true);
    try {
      const response = await axios.delete(`${API}/content-calendar/tasks`, {
        params: {
          accountId: selectedAccount,
          month: selectedMonth,
          year: selectedYear
        }
      });
      toast.success(response.data.message);
      setResetDialogOpen(false);
      
      // Refresh data
      const tasksRes = await axios.get(`${API}/tasks`);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error deleting tasks:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tasks');
    } finally {
      setIsResetting(false);
    }
  };

  // Delete ALL content calendar tasks across all accounts
  const handleDeleteAllTasks = async () => {
    if (!window.confirm('Are you sure you want to delete ALL content calendar tasks for ALL accounts? This cannot be undone.')) {
      return;
    }
    setIsResetting(true);
    try {
      const response = await axios.delete(`${API}/content-calendar/all-tasks`);
      toast.success(response.data.message);
      
      // Refresh data
      const tasksRes = await axios.get(`${API}/tasks`);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error deleting all tasks:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tasks');
    } finally {
      setIsResetting(false);
    }
  };

  // Open edit dialog for a task
  const openEditDialog = (task) => {
    setEditingTask(task);
    setEditForm({
      title: task.title || '',
      dueDate: task.dueDate || '',
      priority: task.priority || 'med',
      status: task.status || 'pending',
      description: task.description || ''
    });
    setEditDialogOpen(true);
    setTaskDetailOpen(false);
  };

  // Save task edits
  const handleSaveTask = async () => {
    if (!editingTask) return;
    try {
      await axios.put(`${API}/tasks/${editingTask.id}`, editForm);
      toast.success('Task updated successfully');
      setEditDialogOpen(false);
      setEditingTask(null);
      
      // Refresh tasks
      const tasksRes = await axios.get(`${API}/tasks`);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Create a new task
  const handleCreateTask = async () => {
    if (!createForm.title || !createForm.dueDate) {
      toast.error('Please provide a title and due date');
      return;
    }
    if (!selectedAccount) {
      toast.error('Please select an account first');
      return;
    }
    
    setIsCreating(true);
    try {
      await axios.post(`${API}/tasks/create-single`, {
        ...createForm,
        accountId: selectedAccount
      });
      
      toast.success('Task created successfully');
      setCreateDialogOpen(false);
      setCreateForm({
        title: '',
        description: '',
        dueDate: '',
        week: 'Week 1',
        priority: 'med',
        broadCategory: 'Social Media',
        platform: 'Instagram + Facebook',
        contentType: 'Social Post',
        isBatchedTask: true,
        workflowType: 'static',
        postCount: 1
      });
      
      // Refresh tasks
      const tasksRes = await axios.get(`${API}/tasks`);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setIsCreating(false);
    }
  };

  // Open create dialog with defaults for current month
  const openCreateDialog = () => {
    // Set default due date to 15th of selected month
    const defaultDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-15`;
    setCreateForm({
      ...createForm,
      dueDate: defaultDate
    });
    setCreateDialogOpen(true);
  };

  // Delete single task
  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      setTaskDetailOpen(false);
      setSelectedTask(null);
      
      // Refresh tasks
      const tasksRes = await axios.get(`${API}/tasks`);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  // Filter tasks for selected account and month
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (task.accountId !== selectedAccount) return false;
      if (!task.dueDate) return false;
      
      const taskDate = new Date(task.dueDate);
      return taskDate.getMonth() + 1 === selectedMonth && 
             taskDate.getFullYear() === selectedYear;
    });
  }, [tasks, selectedAccount, selectedMonth, selectedYear]);

  // Get all tasks for the account (regardless of date) to show in "All Tasks" view
  const allAccountTasks = useMemo(() => {
    return tasks.filter(task => task.accountId === selectedAccount);
  }, [tasks, selectedAccount]);

  // Check which months have tasks for this account
  const monthsWithTasks = useMemo(() => {
    const months = new Set();
    allAccountTasks.forEach(task => {
      if (task.dueDate) {
        const date = new Date(task.dueDate);
        months.add(`${date.getFullYear()}-${date.getMonth() + 1}`);
      }
    });
    return months;
  }, [allAccountTasks]);

  // Group tasks by week
  const tasksByWeek = useMemo(() => {
    const grouped = { 'Ideation': [], 'Week 1': [], 'Week 2': [], 'Week 3': [], 'Week 4': [], 'Week 5': [] };
    filteredTasks.forEach(task => {
      const week = task.week || 'Week 1';
      if (task.isIdeationTask) {
        grouped['Ideation'].push(task);
      } else if (grouped[week]) {
        grouped[week].push(task);
      } else {
        grouped['Week 1'].push(task);
      }
    });
    return grouped;
  }, [filteredTasks]);

  // Group tasks by category
  const tasksByCategory = useMemo(() => {
    const grouped = {};
    CATEGORIES.forEach(cat => grouped[cat.name] = []);
    filteredTasks.forEach(task => {
      const category = task.broadCategory || 'General';
      if (grouped[category]) {
        grouped[category].push(task);
      } else {
        grouped['General'].push(task);
      }
    });
    return grouped;
  }, [filteredTasks]);

  const getCategoryStyle = (category) => {
    const cat = CATEGORIES.find(c => c.name === category);
    return cat?.color || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'done': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'in progress': return <Clock className="h-4 w-4 text-blue-500" />;
      case 'on hold': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <div className="h-4 w-4 rounded-full border-2 border-gray-300" />;
    }
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const selectedAccountData = accounts.find(a => a.id === selectedAccount);
  const accountTemplate = getAccountTemplate();
  const years = [2025, 2026, 2027, 2028];

  // Task Card Component
  const TaskCard = ({ task }) => {
    // Get current stage info for batched tasks
    const getCurrentStageInfo = () => {
      if (!task.isBatchedTask || !task.workflowStages) return null;
      const currentStage = task.workflowStages[task.currentStageIndex || 0];
      return currentStage;
    };
    
    const currentStage = getCurrentStageInfo();
    
    return (
      <div
        className={`p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md relative group ${getCategoryStyle(task.broadCategory)}`}
      >
        {/* Quick delete button - visible on hover */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteTask(task.id);
          }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs hover:bg-red-600"
          title="Delete task"
          data-testid={`quick-delete-${task.id}`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
        
        <div 
          onClick={() => {
            setSelectedTask(task);
            setTaskDetailOpen(true);
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getStatusIcon(task.status)}
                <span className="font-medium text-sm truncate">{task.title}</span>
              </div>
              
              {/* Workflow Stages Progress with Due Dates */}
              {task.isBatchedTask && task.workflowStages && (
                <div className="flex items-center gap-1 mt-2 mb-2 flex-wrap">
                  {task.workflowStages.map((stage, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        stage.status === 'done' ? 'bg-green-200 text-green-800' :
                        stage.status === 'in_progress' ? 'bg-blue-200 text-blue-800 animate-pulse' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {stage.name}
                        {stage.dueDate && (
                          <span className="ml-1 opacity-70">
                            ({new Date(stage.dueDate).getDate()})
                          </span>
                        )}
                      </div>
                      {idx < task.workflowStages.length - 1 && (
                        <span className="text-gray-400 mx-0.5">→</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center gap-2 text-xs opacity-75">
                <span>Publish: {task.dueDate}</span>
                {task.postCount && (
                  <>
                    <span>•</span>
                    <span>{task.postCount} posts</span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Current assignee */}
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-3 w-3 opacity-50" />
              <span className="text-xs opacity-75">
                {currentStage ? (
                  <span className="font-medium">{getMemberName(currentStage.assigneeId)}'s turn</span>
                ) : (
                  task.responsible?.map(id => getMemberName(id)).join(', ') || 'Unassigned'
                )}
              </span>
            </div>
            {currentStage && (
              <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-700">
                {currentStage.name}
              </Badge>
            )}
          </div>
          
          {task.isIdeationTask && (
            <Badge className="mt-2 text-[10px] bg-violet-200 text-violet-800">
              📋 Ideation Task
            </Badge>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Content Calendar</h1>
          <p className="text-muted-foreground mt-1">
            View and manage monthly content for each account
          </p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Select Account
              </label>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger data-testid="account-select">
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => {
                    const accTasks = tasks.filter(t => t.accountId === account.id);
                    return (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: account.color }}
                          />
                          {account.name}
                          <span className="text-xs text-muted-foreground">({accTasks.length})</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedAccount && (
                <p className="text-xs text-muted-foreground mt-1">
                  Total: {allAccountTasks.length} tasks for this account
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Select 
                value={selectedMonth.toString()} 
                onValueChange={(v) => setSelectedMonth(parseInt(v))}
              >
                <SelectTrigger className="w-[130px]" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, idx) => (
                    <SelectItem key={idx} value={(idx + 1).toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={selectedYear.toString()} 
                onValueChange={(v) => setSelectedYear(parseInt(v))}
              >
                <SelectTrigger className="w-[100px]" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Create Single Task Button */}
            <Button 
              variant="outline"
              onClick={openCreateDialog}
              disabled={!selectedAccount}
              className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
              data-testid="create-task-btn"
            >
              <Plus className="h-4 w-4" />
              Create Task
            </Button>

            <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="generate-tasks-btn"
                  disabled={!accountTemplate}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Generate Tasks
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Generate Monthly Tasks</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-muted-foreground">
                    This will create all content tasks for <strong>{MONTHS[selectedMonth - 1]} {selectedYear}</strong> for <strong>{selectedAccountData?.name}</strong>.
                  </p>
                  <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Tasks will be created based on the account's content calendar template, including:
                    </p>
                    <ul className="text-sm text-muted-foreground list-disc list-inside">
                      <li><strong>1 Ideation Task</strong> (due Friday before month)</li>
                      <li>Weekly batched tasks for Social Media, Video, Ads</li>
                      <li>Automatic workflow stage assignments</li>
                    </ul>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleGenerateTasks} 
                    disabled={isGenerating}
                    data-testid="confirm-generate-btn"
                  >
                    {isGenerating ? 'Generating...' : 'Generate Tasks'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Reset All Tasks Button */}
            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 text-red-600 border-red-300 hover:bg-red-50">
                  <RefreshCw className="h-4 w-4" />
                  Delete Month Tasks
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Tasks for {MONTHS[selectedMonth - 1]} {selectedYear}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-muted-foreground">
                    This will <strong className="text-red-600">delete all content calendar tasks</strong> for <strong>{selectedAccountData?.name}</strong> in <strong>{MONTHS[selectedMonth - 1]} {selectedYear}</strong>.
                  </p>
                  <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700 font-medium">This will delete:</p>
                    <ul className="text-sm text-red-600 list-disc list-inside mt-2">
                      <li>{filteredTasks.filter(t => t.isBatchedTask).length} batched workflow tasks</li>
                      <li>{filteredTasks.filter(t => t.isIdeationTask).length} ideation tasks</li>
                    </ul>
                    <p className="text-sm text-red-700 mt-2">You can regenerate them using "Generate Tasks" button after deletion.</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleResetAllTasks} 
                    disabled={isResetting || !selectedAccount}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-reset-btn"
                  >
                    {isResetting ? 'Deleting...' : 'Yes, Delete Tasks'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete ALL Tasks Button */}
            <Button 
              variant="outline" 
              className="gap-2 text-red-800 border-red-500 hover:bg-red-100 font-semibold"
              onClick={handleDeleteAllTasks}
              disabled={isResetting}
            >
              <RefreshCw className="h-4 w-4" />
              {isResetting ? 'Deleting...' : 'Delete ALL Tasks'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {selectedAccount && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-gradient-to-br from-violet-50 to-white">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold text-violet-700">{filteredTasks.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground">Done</p>
              <p className="text-xl font-bold text-green-700">
                {filteredTasks.filter(t => t.status?.toLowerCase() === 'done').length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground">In Progress</p>
              <p className="text-xl font-bold text-blue-700">
                {filteredTasks.filter(t => t.status?.toLowerCase() === 'in progress').length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-gray-50 to-white">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="text-xl font-bold text-gray-700">
                {filteredTasks.filter(t => !t.status || t.status?.toLowerCase() === 'pending').length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-white">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground">Awaiting Ideation</p>
              <p className="text-xl font-bold text-yellow-700">
                {filteredTasks.filter(t => t.ideationStatus === 'pending').length}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Board */}
      {selectedAccount && filteredTasks.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                {MONTHS[selectedMonth - 1]} {selectedYear} - {selectedAccountData?.name}
              </CardTitle>
              <Tabs value={viewMode} onValueChange={setViewMode}>
                <TabsList className="h-8">
                  <TabsTrigger value="week" className="text-xs px-3 py-1">By Week</TabsTrigger>
                  <TabsTrigger value="category" className="text-xs px-3 py-1">By Category</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {viewMode === 'week' ? (
              /* Week View - Horizontal Kanban */
              <div className="overflow-x-auto">
                <div className="flex gap-4 min-w-max pb-4">
                  {Object.entries(tasksByWeek).map(([week, weekTasks]) => (
                    weekTasks.length > 0 && (
                      <div key={week} className="w-[280px] flex-shrink-0">
                        <div className={`mb-3 p-2 rounded-lg font-semibold text-sm flex items-center justify-between ${
                          week === 'Ideation' ? 'bg-violet-100 text-violet-800' : 'bg-gray-100 text-gray-700'
                        }`}>
                          <span>{week}</span>
                          <Badge variant="secondary" className="text-xs">
                            {weekTasks.length}
                          </Badge>
                        </div>
                        <div className="space-y-3">
                          {weekTasks.map(task => (
                            <TaskCard key={task.id} task={task} />
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            ) : (
              /* Category View - Vertical Kanban */
              <div className="overflow-x-auto">
                <div className="flex gap-4 min-w-max pb-4">
                  {CATEGORIES.map(category => {
                    const catTasks = tasksByCategory[category.name] || [];
                    if (catTasks.length === 0) return null;
                    return (
                      <div key={category.name} className="w-[280px] flex-shrink-0">
                        <div className={`mb-3 p-2 rounded-lg font-semibold text-sm flex items-center justify-between border ${category.color}`}>
                          <span>{category.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {catTasks.length}
                          </Badge>
                        </div>
                        <div className="space-y-3">
                          {catTasks.map(task => (
                            <TaskCard key={task.id} task={task} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground font-medium">Status:</span>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-gray-300" /> Pending</div>
                  <div className="flex items-center gap-1"><Clock className="h-3 w-3 text-blue-500" /> In Progress</div>
                  <div className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-500" /> Done</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task Detail Dialog */}
      <Dialog open={taskDetailOpen} onOpenChange={setTaskDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {selectedTask?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {selectedTask.broadCategory && (
                  <Badge className={getCategoryStyle(selectedTask.broadCategory)}>
                    {selectedTask.broadCategory}
                  </Badge>
                )}
                {selectedTask.platform && (
                  <Badge variant="outline">{selectedTask.platform}</Badge>
                )}
                {selectedTask.isIdeationTask && (
                  <Badge className="bg-violet-100 text-violet-700">📋 Ideation</Badge>
                )}
                <Badge className={`${
                  selectedTask.status?.toLowerCase() === 'done' ? 'bg-green-100 text-green-700' :
                  selectedTask.status?.toLowerCase() === 'in progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {selectedTask.status || 'Pending'}
                </Badge>
                <Badge className={`${
                  selectedTask.priority === 'high' ? 'bg-red-100 text-red-700' :
                  selectedTask.priority === 'med' ? 'bg-orange-100 text-orange-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {selectedTask.priority?.toUpperCase() || 'MED'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Due Date</p>
                  <p className="font-medium">{selectedTask.dueDate}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{selectedTask.postCount ? 'Posts' : 'Week'}</p>
                  <p className="font-medium">{selectedTask.postCount ? `${selectedTask.postCount} posts` : (selectedTask.week || 'N/A')}</p>
                </div>
              </div>

              {/* Workflow Stages for Batched Tasks */}
              {selectedTask.isBatchedTask && selectedTask.workflowStages && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold">Workflow Progress</p>
                  <div className="space-y-2">
                    {selectedTask.workflowStages.map((stage, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border-2 ${
                        stage.status === 'done' ? 'bg-green-50 border-green-300' :
                        stage.status === 'in_progress' ? 'bg-blue-50 border-blue-300' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{stage.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{getMemberName(stage.assigneeId)}</span>
                            {stage.dueDate && (
                              <span className="text-[10px] text-muted-foreground">
                                Due: {format(new Date(stage.dueDate), 'MMM d')}
                              </span>
                            )}
                            <Badge className={`text-[10px] ${
                              stage.status === 'done' ? 'bg-green-200 text-green-800' :
                              stage.status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
                              'bg-gray-200 text-gray-600'
                            }`}>
                              {stage.status === 'done' ? '✓ Done' : stage.status === 'in_progress' ? 'In Progress' : 'Pending'}
                            </Badge>
                          </div>
                        </div>
                        {/* Show deliverables from completed stages */}
                        {stage.deliverables && (
                          <div className="mt-2 pt-2 border-t border-dashed">
                            {stage.deliverables.note && (
                              <p className="text-xs text-gray-600">{stage.deliverables.note}</p>
                            )}
                            {stage.deliverables.links && stage.deliverables.links.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {stage.deliverables.links.map((link, linkIdx) => (
                                  <a
                                    key={linkIdx}
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    🔗 {link.includes('drive.google') ? 'Google Drive' : `Link ${linkIdx + 1}`}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Show ideation reference */}
                  {selectedTask.ideationReference && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs font-semibold text-amber-700 mb-1">📋 Ideation/Content Reference</p>
                      {selectedTask.ideationReference.note && (
                        <p className="text-xs text-gray-600">{selectedTask.ideationReference.note}</p>
                      )}
                      {selectedTask.ideationReference.links?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedTask.ideationReference.links.map((link, linkIdx) => (
                            <a
                              key={linkIdx}
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              🔗 {link.includes('drive.google') ? 'Google Drive' : 
                                   link.includes('docs.google') ? 'Google Doc' : `Link ${linkIdx + 1}`}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Regular RACI for non-batched tasks */}
              {!selectedTask.isBatchedTask && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Responsible</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedTask.responsible?.map(id => (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {getMemberName(id)}
                        </Badge>
                      )) || <span className="text-gray-400">None</span>}
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Accountable</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedTask.accountable?.map(id => (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {getMemberName(id)}
                        </Badge>
                      )) || <span className="text-gray-400">None</span>}
                    </div>
                  </div>
                </div>
              )}

              {selectedTask.ideationTaskId && (
                <div className={`p-3 rounded-lg ${
                  selectedTask.ideationStatus === 'complete' 
                    ? 'bg-violet-50 border border-violet-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <p className="text-xs font-semibold mb-1">
                    {selectedTask.ideationStatus === 'complete' 
                      ? '📋 Ideation Reference' 
                      : '⏳ Awaiting Ideation'}
                  </p>
                  {selectedTask.ideationReference?.note && (
                    <p className="text-sm">{selectedTask.ideationReference.note}</p>
                  )}
                  {selectedTask.ideationReference?.links?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedTask.ideationReference.links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-violet-600 hover:underline"
                        >
                          🔗 Link {idx + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedTask.instructions && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Instructions</p>
                  <p className="text-sm bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {selectedTask.instructions}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => openEditDialog(selectedTask)}
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                Edit Task
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleDeleteTask(selectedTask.id)}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Delete
              </Button>
            </div>
            <Button variant="outline" onClick={() => setTaskDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <input
                type="text"
                value={editForm.title || ''}
                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <input
                type="date"
                value={editForm.dueDate || ''}
                onChange={(e) => setEditForm({...editForm, dueDate: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <select
                value={editForm.priority || 'med'}
                onChange={(e) => setEditForm({...editForm, priority: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="high">High</option>
                <option value="med">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                value={editForm.status || 'pending'}
                onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTask}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Create New Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Title *</Label>
              <Input
                type="text"
                value={createForm.title}
                onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                className="mt-1"
                placeholder="e.g., Week 2 Video Content"
                data-testid="create-task-title"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Due Date (Publish) *</Label>
                <Input
                  type="date"
                  value={createForm.dueDate}
                  onChange={(e) => setCreateForm({...createForm, dueDate: e.target.value})}
                  className="mt-1"
                  data-testid="create-task-date"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Week</Label>
                <Select 
                  value={createForm.week} 
                  onValueChange={(v) => setCreateForm({...createForm, week: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Week 1">Week 1</SelectItem>
                    <SelectItem value="Week 2">Week 2</SelectItem>
                    <SelectItem value="Week 3">Week 3</SelectItem>
                    <SelectItem value="Week 4">Week 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Priority</Label>
                <Select 
                  value={createForm.priority} 
                  onValueChange={(v) => setCreateForm({...createForm, priority: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="med">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Category</Label>
                <Select 
                  value={createForm.broadCategory} 
                  onValueChange={(v) => setCreateForm({...createForm, broadCategory: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="isBatched"
                  checked={createForm.isBatchedTask}
                  onChange={(e) => setCreateForm({...createForm, isBatchedTask: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="isBatched" className="text-sm font-medium text-violet-800 cursor-pointer">
                  Batched Workflow Task
                </Label>
              </div>
              
              {createForm.isBatchedTask && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-violet-700">Workflow Type</Label>
                    <Select 
                      value={createForm.workflowType} 
                      onValueChange={(v) => setCreateForm({...createForm, workflowType: v})}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="static">Static (Content → Design → Review)</SelectItem>
                        <SelectItem value="video">Video (Ideation → Shoot → Edit → Review)</SelectItem>
                        <SelectItem value="ad">Ad (Content → Design → Review)</SelectItem>
                        <SelectItem value="text">Text (Content → Review)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-violet-700">Post Count</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={createForm.postCount}
                      onChange={(e) => setCreateForm({...createForm, postCount: parseInt(e.target.value) || 1})}
                      className="mt-1 h-8 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">Description (optional)</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                className="mt-1"
                rows={2}
                placeholder="Additional details..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTask} 
              disabled={isCreating || !createForm.title || !createForm.dueDate}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-create-task"
            >
              {isCreating ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* No tasks message */}
      {selectedAccount && filteredTasks.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground mb-2">
              No tasks found for {MONTHS[selectedMonth - 1]} {selectedYear}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              This account has {allAccountTasks.length} total tasks, but none are scheduled for this month.
            </p>
            <Button onClick={() => setGenerateDialogOpen(true)} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Generate Tasks for {MONTHS[selectedMonth - 1]} {selectedYear}
            </Button>
            {monthsWithTasks.size > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">Months with tasks:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {Array.from(monthsWithTasks).sort().map(m => {
                    const [y, mon] = m.split('-');
                    return (
                      <Button 
                        key={m} 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedYear(parseInt(y));
                          setSelectedMonth(parseInt(mon));
                        }}
                      >
                        {MONTHS[parseInt(mon) - 1]} {y}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContentCalendar;
